
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import formatDateTime from "../../../helpers/formatDateTime";
import {
  MILESTONE_REVISION_LOG,
  OPTIONAL_MILESTONE_REVISION_LOG,
} from "../types/CommonTypes";

type RevisedMilestoneListProps = {
  currentMilestoneRevisons?: Array<
    MILESTONE_REVISION_LOG | OPTIONAL_MILESTONE_REVISION_LOG
  >;
};

function isRevision(
  item: MILESTONE_REVISION_LOG | OPTIONAL_MILESTONE_REVISION_LOG
): item is MILESTONE_REVISION_LOG {
  return "milestoneRevisedStartTime" in item;
}

const getDisplayName = (
  ...candidates: Array<string | null | undefined>
): string => {
  const value = candidates.find(
    (item) => typeof item === "string" && item.trim()
  );
  return value ?? "NA";
};

export default function RevisedMilestoneList(
  props: Readonly<RevisedMilestoneListProps>
) {
  const { t } = useTranslation();
  const { currentMilestoneRevisons = [] } = props;

  const revision = [...currentMilestoneRevisons].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime()
  );

  if (revision.length === 0) {
    return (
      <Typography color="custom.foreground.inactive" fontSize={16}>
        {t("No_revisions_yet")}
      </Typography>
    );
  }
  return (
    <Box>
      {/* Revision History Heading */}
      <Typography sx={styles.historyTitle}>
        {t("Revision_History")} ({(revision.length??0)-1})
      </Typography>

      {/* Revision Items */}
      {revision.map((item, index) => {
        const isRevisionType = isRevision(item);
        const isRoomCleanStart =
          isRevisionType && item?.action === "start";
        const isRoomCleanEnd =
          isRevisionType && item?.action === "end";

        const time = isRoomCleanStart
          ? item?.milestoneRevisedStartTime
          : item?.milestoneRevisedEndTime;

        let name: string;
        if (isRoomCleanStart) {
          name = getDisplayName(
            item?.startTimeLoggedBy,
            item?.milestoneRevisedByUserName
          );
        } else if (isRoomCleanEnd) {
          name = getDisplayName(
            item?.endTimeLoggedBy,
            item?.milestoneRevisedByUserName
          );
        } else {
          name = getDisplayName(item?.milestoneRevisedByUserName);
        }

        const { skipReason, skipReasonNote,comment } =
          item as MILESTONE_REVISION_LOG;

        const action = (item as { action?: string })?.action;
        const skipped = action === "skip";

        const comments = comment ?? item?.comment;

        return (
          <Box
            key={item?.id ?? index}
            sx={{
              ...styles.revisionItem,
              mb: index === revision.length - 1 ? 0 : 1.5,
            }}
          >
            {/* Revision Time and User */}
            <Typography sx={styles.revisionLine}>
              <Typography component="span" sx={styles.revisionTime}>
                {formatDateTime(time, true, "hh:mm:ss aaa")}
              </Typography>

              <Typography component="span" sx={styles.arrow}>
                {" → "}
              </Typography>

              <Typography component="span" sx={styles.revisionDetails}>
                {skipped ? t("Skip_by") : t("Revised_by")}{" "}
                {name} {t("at")}{" "}
                {formatDateTime(item?.createdAt, true, "hh:mm:ss aaa")}
              </Typography>
            </Typography>

            {/* Comments */}
            {comments != null && (
              <Box sx={styles.reasonBox}>
                <Typography sx={styles.reasonLabel}>
                  {t("Comment")}
                </Typography>
                <Typography sx={styles.reasonText}>
                  {comments}
                </Typography>
              </Box>
            )}

            {/* Skip Reason */}
            {skipped && (skipReasonNote ?? skipReason) && (
              <Box sx={styles.reasonBox}>
                <Typography sx={styles.reasonLabel}>
                  {t("Reason")}
                </Typography>
                <Typography sx={styles.reasonText}>
                  {skipReasonNote ?? skipReason}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

const styles = {
  historyTitle: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    color: "custom.foreground.primary",
    mb: 1,
  },

  revisionItem: {
    width: "100%",
  },

  revisionLine: {
    fontSize: "13px",
    lineHeight: "20px",
    color: "custom.foreground.primary",
  },

  revisionTime: {
    fontSize: "13px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  arrow: {
    fontSize: "14px",
    fontWeight: 400,
    mx: 0.5,
  },

  revisionDetails: {
    fontSize: "13px",
    fontWeight: 400,
  },


  reasonBox: {
    width: "100%",
    boxSizing: "border-box",
    mt: "8px",
    border: "1px solid #E1E4EA",
    borderRadius: "8px",
    px: "10px",
    py: "8px",
    backgroundColor: "#F8F9FA",
  },

  reasonLabel: {
    fontSize: "11px",
    lineHeight: "15px",
    color: "#9299AA",
    mb: "2px",
  },

  reasonText: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: "custom.foreground.primary",
  },
};
